import { FC, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";

import Button from "@/components/Button";
import Text from "@/components/Text";
import {
  ImageGenerationProjectResource,
  ImageQueryResource,
  ImageToolStyleDescriptionResource,
} from "@/types/resources";
import { api } from "@/utils/api";
import useAlerts from "@/hooks/useAlerts";
import {
  Input,
  Label,
  RadioCircle,
  RadioInput,
  Radios,
  RadiosLabel,
  Textarea,
} from "@/components/Forms";
import FormField from "@/components/Forms/FormField";
import ShowHideButton from "@/components/ShowHideButton";
import { ImageGenerationProjectStatus, ImageQueryType } from "@/utils/enums";
import ImageToolQuery from "./ImageToolQuery";
import useToasts from "@/hooks/useToasts";

interface FormDataT {
  style_description?: string;
  aspect_ratio: string;
  style_template: string;
  image: string;
}

const aspectRatios = ["1:1", "2:3", "3:2", "5:4", "4:7", "7:4"];

const Box = styled.div`
  padding: 24px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
`;

const BoxHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BoxContent = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const Heading = styled(Text)`
  margin-bottom: 4px;
`;

const QueryHolder = styled.div`
  margin-bottom: 16px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const ButtonHolder = styled.div`
  text-align: right;
  margin-top: 32px;
  margin-bottom: 56px;
`;

const StyledButton = styled(Button)`
  padding: 0 32px;
`;

const InputHolder = styled.div`
  position: relative;
`;

const AddButton = styled.button`
  font-weight: 700;
  font-size: 14px;
  line-height: 22px;
  font-family: "Manrope", sans-serif;
  color: ${({ theme }) => theme.colors.purple};
  transition: opacity ease-out 0.2s;
  position: absolute;
  right: 20px;
  top: calc(50% - 11px);
  &:hover {
    opacity: 0.8;
  }
`;

const StyledLabel = styled(Label)`
  margin-bottom: 12px;
`;

const Queries = styled.div`
  margin-top: 24px;
`;

const Parameters = styled.div`
  margin-top: 32px;
`;

const RatioBox = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  padding: 24px;
  margin-top: 32px;
`;

const PlaceholderDiv = styled.div`
  margin-bottom: 24px;
  width: 100%;
`;

const NoQueriesPlaceholder = styled.div`
  height: 134px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  margin-bottom: 8px;
  margin-top: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NoQueriesText = styled(Text)`
  display: flex;
  align-items: center;
  svg {
    margin-right: 8px;
  }
`;

const StyledInput = styled(Input)`
  padding-right: 70px;
`;

const ImageField = styled(FormField<FormDataT>)`
  margin-top: 32px;
`;

interface Props {
  project: ImageGenerationProjectResource;
  title: string;
  subtitle: string;
  queriesType: ImageQueryType;
  refetch: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<QueryObserverResult<ImageGenerationProjectResource, unknown>>;
}

const ImageToolQueriesBox: FC<Props> = ({
  project,
  title,
  subtitle,
  queriesType,
  refetch,
}) => {
  const { id } = useParams();

  const [isLoadingImages, setIsLoadingImages] = useState(false);

  const toasts = useToasts();

  const getImagesType = () => {
    switch (queriesType) {
      case ImageQueryType.SEARCH:
        return "stocks";
      case ImageQueryType.PROMPTS:
        return "midjourney";
      case ImageQueryType.HASHTAGS:
        return "instagram";
      case ImageQueryType.GETTY:
        return "getty";
    }
  };

  const [showContent, setShowContent] = useState(true);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const { data: descriptions } = useQuery<ImageToolStyleDescriptionResource[]>(
    ["image_generation_descriptions"],
    () =>
      api.get<never, ImageToolStyleDescriptionResource[]>(
        "/v1/image-generation-descriptions"
      ),
    {
      staleTime: 1000 * 120,
    }
  );

  const inputRef = useRef<HTMLInputElement>(null);

  const onGetImages = async () => {
    setIsLoadingImages(true);

    await api.post(`/v1/image-generation-projects/${project.id}/search`, {
      regenerate:
        project.statuses[getImagesType()] === ImageGenerationProjectStatus.DONE,
    });

    await queryClient.invalidateQueries(["image_generation_project", { id }], {
      exact: true,
    });

    setIsLoadingImages(false);

    setTimeout(refetch, 5000);
  };

  const onGetGettyImages = async () => {
    setIsLoadingImages(true);

    await api.post(`/v1/image-generation-projects/${project.id}/getty`, {
      regenerate:
        project.statuses[getImagesType()] === ImageGenerationProjectStatus.DONE,
    });

    await queryClient.invalidateQueries(["image_generation_project", { id }], {
      exact: true,
    });

    setIsLoadingImages(false);

    setTimeout(refetch, 5000);
  };

  const onGetInstagramImages = async () => {
    setIsLoadingImages(true);

    await api.post(`/v1/image-generation-projects/${project.id}/instagram`, {
      regenerate:
        project.statuses[getImagesType()] === ImageGenerationProjectStatus.DONE,
    });

    await queryClient.invalidateQueries(["image_generation_project", { id }], {
      exact: true,
    });

    setIsLoadingImages(false);

    setTimeout(refetch, 5000);
  };

  const { register, control, handleSubmit, setValue } = useForm<FormDataT>({
    defaultValues: {
      image: "",
      aspect_ratio: project.aspectRatio ?? "1:1",
      style_description: project.styleDescription ?? "",
    },
  });

  const onGetMJImages = handleSubmit(
    async ({ aspect_ratio, style_description, image }) => {
      try {
        setIsLoadingImages(true);

        await api.post(
          `/v1/image-generation-projects/${project.id}/midjourney`,
          {
            image,
            aspect_ratio,
            style_description: style_description ?? "",
            regenerate:
              project.statuses[getImagesType()] ===
              ImageGenerationProjectStatus.DONE,
          }
        );

        await queryClient.invalidateQueries(
          ["image_generation_project", { id }],
          {
            exact: true,
          }
        );

        setIsLoadingImages(false);

        setTimeout(refetch, 5000);
      } catch (err) {
        alerts.error();
      }
    }
  );

  const onQueryAdd = async () => {
    const toastId = toasts.loading();

    try {
      const query = inputRef.current?.value;

      if (!query || query?.length < 1) return toasts.error(toastId);

      await api.post("/v1/image-queries", {
        projectId: project.id,
        type: queriesType,
        query,
      });

      await queryClient.invalidateQueries(
        ["image_generation_project", { id }],
        {
          exact: true,
        }
      );

      inputRef.current.value = "";

      toasts.success(toastId);
    } catch (_) {
      toasts.error(toastId);
    }
  };

  const aspectRatiovalue = useWatch({
    name: "aspect_ratio",
    control,
  });

  const options = descriptions?.map((description) => {
    return {
      label: description.title,
      value: description.description,
    };
  });

  const selectedDescription = useWatch({
    name: "style_template",
    control,
  });

  useEffect(() => {
    setValue("style_template", project.styleDescription ?? "");
  }, [project.styleDescription, setValue]);

  useEffect(() => {
    setValue("style_description", selectedDescription);
  }, [selectedDescription, setValue]);

  const onSubmit = () => {
    switch (queriesType) {
      case ImageQueryType.SEARCH:
        return onGetImages();
      case ImageQueryType.GETTY:
        return onGetGettyImages();
      case ImageQueryType.PROMPTS:
        return onGetMJImages();
      case ImageQueryType.HASHTAGS:
        return onGetInstagramImages();
    }
  };

  const groupedQueries = project.queries?.reduce<{
    [key: number]: ImageQueryResource[];
  }>((acc, item) => {
    if (!acc[item.type]) {
      acc[item.type] = [];
    }
    acc[item.type].push(item);
    return acc;
  }, {});

  return (
    <>
      <Box>
        <BoxHeader>
          <div>
            <Heading $type="button">{title}</Heading>
            <Text $type="smallHeadline" $color="gray">
              {subtitle}
            </Text>
          </div>
          <ShowHideButton
            showContent={showContent}
            onClick={() => setShowContent((prev) => !prev)}
          />
        </BoxHeader>
        {showContent && (
          <BoxContent>
            <InputHolder>
              <StyledInput
                ref={inputRef}
                placeholder={`Add ${
                  queriesType === ImageQueryType.PROMPTS ? "prompt" : "query"
                }`}
                onKeyUp={(e) => {
                  if (e.key !== "Enter") {
                    return;
                  }
                  onQueryAdd();
                }}
              />
              <AddButton onClick={onQueryAdd}>Add</AddButton>
            </InputHolder>
            {!!groupedQueries?.[queriesType]?.length ? (
              <Queries>
                {groupedQueries?.[queriesType].map((query, index) => (
                  <QueryHolder key={index}>
                    <ImageToolQuery index={index} query={query} />
                    {!!query.queries?.length &&
                      query.queries.map((child, childIndex) => (
                        <ImageToolQuery
                          key={`child-${childIndex}`}
                          index={index}
                          childIndex={childIndex + 1}
                          query={child}
                          child
                        />
                      ))}
                  </QueryHolder>
                ))}
              </Queries>
            ) : (
              <NoQueriesPlaceholder>
                <NoQueriesText $color="gray" $type="caption">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.40625 6.66602H13.3322"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M7.40625 9.62891H13.3322"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M7.40625 12.5918H10.3692"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M13.025 18.5175H2.96144C2.76499 18.5175 2.57658 18.4359 2.43766 18.2907C2.29875 18.1455 2.2207 17.9485 2.2207 17.7431V2.25488C2.2207 2.04949 2.29875 1.85252 2.43766 1.70729C2.57658 1.56206 2.76499 1.48047 2.96144 1.48047H17.7763C17.9727 1.48047 18.1611 1.56206 18.3 1.70729C18.439 1.85252 18.517 2.04949 18.517 2.25488V12.7759C18.517 12.8776 18.4978 12.9783 18.4606 13.0722C18.4234 13.1662 18.3688 13.2515 18.3 13.3235L13.5488 18.2907C13.48 18.3626 13.3983 18.4196 13.3085 18.4586C13.2186 18.4975 13.1223 18.5175 13.025 18.5175V18.5175Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M18.4502 12.5918H13.332V17.7103"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  No queries added
                </NoQueriesText>
              </NoQueriesPlaceholder>
            )}
            {queriesType === ImageQueryType.PROMPTS && (
              <Parameters>
                <form onSubmit={onGetMJImages}>
                  <PlaceholderDiv>
                    <FormField<FormDataT, string>
                      control={control}
                      name="style_template"
                      label="Description Templates"
                      type="select"
                      options={
                        !!options?.length
                          ? [{ label: "None", value: "" }, ...options]
                          : []
                      }
                    />
                  </PlaceholderDiv>
                  <StyledLabel htmlFor="description_field">
                    Description
                  </StyledLabel>
                  <Textarea
                    id="description_field"
                    {...register("style_description")}
                    placeholder="Add description"
                  />
                  <RatioBox>
                    <StyledLabel>Aspect Ratio</StyledLabel>
                    <Radios>
                      {aspectRatios.map((aspectRatio, index) => (
                        <RadiosLabel key={index}>
                          {aspectRatio}
                          <RadioInput
                            type="radio"
                            name="selection"
                            checked={aspectRatiovalue === aspectRatio}
                            onChange={() =>
                              setValue("aspect_ratio", aspectRatio)
                            }
                          />
                          <RadioCircle />
                        </RadiosLabel>
                      ))}
                    </Radios>
                  </RatioBox>
                  <ImageField
                    label="Reference Image"
                    id="mj-reference-image"
                    control={control}
                    name="image"
                    type="image"
                    existingMedia={project.midjourneyReferenceImage}
                  />
                </form>
              </Parameters>
            )}
          </BoxContent>
        )}
      </Box>
      <ButtonHolder>
        {showContent && (
          <StyledButton
            loading={isLoadingImages}
            disabled={
              project.statuses[getImagesType()] ===
                ImageGenerationProjectStatus.PENDING ||
              isLoadingImages ||
              !groupedQueries?.[queriesType]?.length
            }
            onClick={() => onSubmit()}
          >
            {project.statuses[getImagesType()] ===
              ImageGenerationProjectStatus.DONE ||
            project.statuses[getImagesType()] ===
              ImageGenerationProjectStatus.PENDING
              ? "Regenerate"
              : "Get images"}
          </StyledButton>
        )}
      </ButtonHolder>
    </>
  );
};

export default ImageToolQueriesBox;
